<template>
  <div
    class="integration-config-page"
    :class="{ 'is-modal': asModal }"
  >
    <PageHeader
      v-if="!asModal"
      :title="displayName ? t('integrations.config.title', { name: displayName }) : t('integrations.config.loading')"
      :summary="t('integrations.config.summary')"
      :back-link-to="{ name: 'shop.Integrations' }"
      :back-link-text="t('integrations.config.backToList')"
    />

    <!-- Modale : header porteur (logo + étape + TITRE de l'étape). Remplace le
         gros bloc d'étape, pour laisser un maximum de place au formulaire. -->
    <header
      v-if="asModal"
      class="modal-topbar"
    >
      <div
        class="modal-topbar-logo"
        :class="{ 'has-image': displayIcon }"
      >
        <img
          v-if="displayIcon"
          :src="displayIcon"
          :alt="displayName"
        >
        <i
          v-else
          class="fas fa-puzzle-piece"
        />
      </div>
      <div class="modal-topbar-heading">
        <span class="modal-topbar-brand">{{ displayName || t('integrations.config.loading') }}</span>
        <span class="modal-topbar-sub">{{ t('integrations.config.summary') }}</span>
      </div>
      <button
        type="button"
        class="modal-topbar-close"
        :aria-label="t('integrations.config.cancel')"
        @click="goBack"
      >
        <i class="fas fa-times" />
      </button>
    </header>

    <div class="page-content">
      <Loader v-if="loading" />

      <div
        v-else-if="error"
        class="error-state"
      >
        <i class="fas fa-exclamation-triangle" />
        <p>{{ error }}</p>
        <SpmButton
          :label="t('integrations.config.retry')"
          class="p-button-primary"
          @click="loadIntegration"
        />
      </div>

      <div
        v-else-if="!integration || !hasConfigFields"
        class="empty-state"
      >
        <i class="fas fa-cog" />
        <p>{{ t('integrations.config.noConfig') }}</p>
      </div>

      <!-- ========== WIZARD MODE ========== -->
      <div
        v-else-if="isWizardMode"
        class="wizard-layout"
      >
        <!-- Left: vertical stepper -->
        <aside class="wizard-sidebar">
          <div class="wizard-sidebar-header">
            <div
              class="wizard-logo"
              :class="{ 'has-image': displayIcon }"
            >
              <img
                v-if="displayIcon"
                :src="displayIcon"
                :alt="displayName"
              >
              <i
                v-else
                class="fas fa-puzzle-piece"
              />
            </div>
            <div class="wizard-sidebar-title">
              {{ displayName }}
            </div>
          </div>

          <nav class="stepper">
            <div
              v-for="(step, index) in wizardSteps"
              :key="step.key"
              class="stepper-item"
              :class="{
                'is-active': index === wizard.currentStepIndex.value,
                'is-complete': wizard.stepStatuses.value[index]?.complete && wizard.stepStatuses.value[index]?.validated,
                'is-locked': !wizard.isStepAccessible(step.key),
              }"
              @click="wizard.goToIndex(index)"
            >
              <div class="stepper-indicator">
                <div class="stepper-circle">
                  <i
                    v-if="wizard.stepStatuses.value[index]?.complete && wizard.stepStatuses.value[index]?.validated"
                    class="fas fa-check"
                  />
                  <i
                    v-else-if="!wizard.isStepAccessible(step.key)"
                    class="fas fa-lock"
                  />
                  <span v-else>{{ index + 1 }}</span>
                </div>
                <div
                  v-if="index < wizardSteps.length - 1"
                  class="stepper-line"
                />
              </div>
              <div class="stepper-content">
                <span class="stepper-label">{{ getLocalizedText(step.label) }}</span>
                <span
                  v-if="step.description"
                  class="stepper-desc"
                >{{ getLocalizedText(step.description) }}</span>
              </div>
            </div>
          </nav>
        </aside>

        <!-- Modal (épuré) : stepper discret — ronds numérotés + connecteurs fins.
             Le libellé n'est affiché que pour l'étape active (titre complet repris
             dans le corps). Étapes verrouillées non cliquables (gating T2). -->
        <nav
          v-if="asModal && isWizardMode"
          class="wizard-stepper"
        >
          <template
            v-for="(step, index) in wizardSteps"
            :key="step.key"
          >
            <button
              type="button"
              class="wstep"
              :class="{
                'is-active': index === wizard.currentStepIndex.value,
                'is-complete': wizard.stepStatuses.value[index]?.complete && wizard.stepStatuses.value[index]?.validated,
                'is-locked': !wizard.isStepAccessible(step.key),
              }"
              :disabled="!wizard.isStepAccessible(step.key)"
              :title="getLocalizedText(step.label)"
              @click="wizard.goToIndex(index)"
            >
              <span class="wstep-dot">{{ index + 1 }}</span>
              <span class="wstep-lbl">{{ getLocalizedText(step.label) }}</span>
            </button>
            <span
              v-if="index < wizardSteps.length - 1"
              class="wstep-bar"
              :class="{ done: wizard.stepStatuses.value[index]?.complete && wizard.stepStatuses.value[index]?.validated }"
            />
          </template>
        </nav>

        <!-- Right: form area -->
        <main class="wizard-main">
          <div class="wizard-card">
            <!-- Step header -->
            <div class="wizard-card-header">
              <div class="wizard-step-badge">
                {{ t('integrations.config.step', { current: wizard.currentStepIndex.value + 1, total: wizardSteps.length }) }}
              </div>
              <h2 class="wizard-card-title">
                {{ getLocalizedText(currentWizardStep?.label) }}
              </h2>
              <p
                v-if="currentWizardStep?.description"
                class="wizard-card-desc"
              >
                {{ getLocalizedText(currentWizardStep.description) }}
              </p>
            </div>

            <!-- Locked -->
            <div
              v-if="!wizard.isStepAccessible(currentWizardStep?.key || '')"
              class="wizard-locked"
            >
              <div class="wizard-locked-icon">
                <i class="fas fa-lock" />
              </div>
              <p>{{ t('integrations.config.stepLocked') }}</p>
            </div>

            <!-- Fields -->
            <form
              v-else
              class="wizard-form"
              @submit.prevent="handleSubmit"
            >
              <div class="wizard-fields">
                <!-- Rendu de champ unifié (G7) — même composant pour wizard & simple. -->
                <ConfigFieldRenderer
                  v-for="field in currentFields"
                  :key="field.key"
                  v-model="formData[field.key]"
                  :field="field"
                  :field-error="fieldErrors[field.key] || ''"
                  :remote-loading="!!remoteDataLoading[field.key]"
                  :remote-error="!!remoteDataError[field.key]"
                  :remote-options="remoteOptions[field.key] || []"
                  @remote-retry="loadRemoteData"
                />
              </div>

              <!-- Actions bar -->
              <div class="wizard-actions">
                <SpmButton
                  v-if="!wizard.isFirstStep.value"
                  :label="t('integrations.config.previous')"
                  type="button"
                  icon="fas fa-arrow-left"
                  class="p-button-secondary p-button-outlined"
                  @click="wizard.prev()"
                />
                <span
                  v-else
                  class="wizard-actions-spacer"
                />

                <SpmButton
                  v-if="!wizard.isLastStep.value"
                  :label="isTesting ? t('integrations.config.testing') : (currentWizardStep?.on_complete ? t('integrations.config.testConnection') : t('integrations.config.next'))"
                  type="submit"
                  :icon="isTesting ? 'fas fa-spinner fa-spin' : (currentWizardStep?.on_complete ? 'fas fa-plug' : 'fas fa-arrow-right')"
                  icon-pos="right"
                  class="p-button-primary"
                  :loading="isSaving || isTesting"
                />
                <SpmButton
                  v-else
                  :label="t('integrations.config.finish')"
                  type="submit"
                  icon="fas fa-check"
                  icon-pos="right"
                  class="p-button-success"
                  :loading="isSaving"
                />
              </div>
            </form>
          </div>
        </main>
      </div>

      <!-- ========== SIMPLE MODE (flat fields) ========== -->
      <div
        v-else
        class="simple-layout"
      >
        <div class="simple-header-card">
          <div
            class="simple-logo"
            :class="{ 'has-image': displayIcon }"
          >
            <img
              v-if="displayIcon"
              :src="displayIcon"
              :alt="displayName"
            >
            <i
              v-else
              class="fas fa-puzzle-piece"
            />
          </div>
          <div>
            <h3>{{ displayName }}</h3>
            <p v-if="displayDescription">
              {{ displayDescription }}
            </p>
          </div>
        </div>

        <div class="simple-form-card">
          <h2>{{ t('integrations.config.formTitle') }}</h2>
          <p class="simple-form-desc">
            {{ t('integrations.config.description') }}
          </p>

          <form
            class="wizard-form"
            @submit.prevent="handleSubmitSimple"
          >
            <div class="wizard-fields">
              <!-- Rendu de champ unifié (G7) — même composant que le mode wizard :
                   multiselect, champs remote, validation et fieldErrors identiques. -->
              <ConfigFieldRenderer
                v-for="field in allFields"
                :key="field.key"
                v-model="formData[field.key]"
                :field="field"
                :field-error="fieldErrors[field.key] || ''"
                :remote-loading="!!remoteDataLoading[field.key]"
                :remote-error="!!remoteDataError[field.key]"
                :remote-options="remoteOptions[field.key] || []"
                @remote-retry="loadRemoteData"
              />
            </div>

            <div class="wizard-actions">
              <SpmButton
                :label="t('integrations.config.cancel')"
                type="button"
                class="p-button-secondary p-button-outlined"
                @click="goBack"
              />
              <SpmButton
                :label="t('integrations.config.save')"
                type="submit"
                icon="fas fa-check"
                class="p-button-success"
                :loading="isSaving"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, onMounted, reactive, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import PageHeader from '@/components/layout/PageHeader.vue';
import Loader from '@/components/layout/Loader.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import ConfigFieldRenderer from '@/views/shop/components/ConfigFieldRenderer.vue';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastError, showToastSuccess } from '@/helpers';
import { UserState } from '@/composables/User';
import { useConfigWizard } from '@/composables/useConfigWizard';
import type { Integration, ConfigField, ConfigStep } from '@/composables/useIntegrations';

export default defineComponent({
  name: 'IntegrationConfig',

  components: {
    PageHeader,
    Loader,
    SpmButton,
    ConfigFieldRenderer,
  },

  props: {
    // T3 — when hosted in the config modal, the slug comes from a prop rather
    // than the route, the page chrome (PageHeader) is hidden, and exits are
    // emitted instead of navigating.
    slug: { type: String, default: '' },
    asModal: { type: Boolean, default: false },
    // Nom / icône affichés immédiatement dans le header modale (avant que le
    // fetch de config ne renvoie l'intégration) — évite un header vide au 1er rendu.
    initialName: { type: String, default: '' },
    initialIcon: { type: String, default: '' },
  },

  emits: ['close', 'done'],

  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const route = useRoute();
    const router = useRouter();

    const idShop = UserState.activeShop?.id ?? 0;
    const integrationSlug = props.slug || (route.params.slug as string);

    const loading = ref(true);
    const error = ref('');
    const integration = ref<Integration | null>(null);

    const displayName = ref(props.initialName || window.history.state?.name || '');
    const displayIcon = ref(props.initialIcon || window.history.state?.icon_url || '');
    const displayDescription = ref(window.history.state?.short_description || '');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formData = ref<Record<string, any>>({});
    const isSaving = ref(false);
    const isTesting = ref(false);

    // Wizard reactive state
    const wizardSteps = ref<ConfigStep[]>([]);
    const savedConfigsMap = ref<Map<string, string>>(new Map());
    const isWizardMode = ref(false);

    // Initialize wizard composable with reactive refs
    const wizard = useConfigWizard(wizardSteps, savedConfigsMap);

    // Validation errors
    const fieldErrors = reactive<Record<string, string>>({});

    /**
     * Mappe les erreurs backend `error.errors[key]` (par champ) vers
     * `fieldErrors[key]` afin que le rendu de champ unifié (G7) affiche l'erreur
     * inline sous le bon champ (au lieu d'un seul toast global). Renvoie le texte
     * concaténé pour un éventuel toast complémentaire.
     */
    const applyBackendFieldErrors = (errors: Record<string, unknown>): string => {
      Object.keys(fieldErrors).forEach((k) => { delete fieldErrors[k]; });
      const messages: string[] = [];
      Object.entries(errors || {}).forEach(([key, val]) => {
        const msg = Array.isArray(val) ? val.join('\n') : String(val);
        fieldErrors[key] = msg;
        messages.push(msg);
      });
      return messages.join('\n');
    };

    // Remote data
    const remoteOptions = reactive<Record<string, Array<{ value: string; label: string }>>>({});
    const remoteDataLoading = reactive<Record<string, boolean>>({});
    const remoteDataError = reactive<Record<string, boolean>>({});

    // ---- Computed ----

    const hasConfigFields = computed(() => {
      if (!integration.value?.config) return false;
      return !!(
        (integration.value.config.steps && integration.value.config.steps.length > 0)
        || (integration.value.config.fields && integration.value.config.fields.length > 0)
      );
    });

    const currentWizardStep = computed(() => wizard.currentStep.value);

    const currentFields = computed((): ConfigField[] => {
      const fields = isWizardMode.value && currentWizardStep.value
        ? currentWizardStep.value.fields || []
        : integration.value?.config?.fields || [];
      // Integrator-owned fields (`owner: 'integrator'`) are set by the
      // integrator via the API per shop — never shown to / editable by the merchant.
      return fields.filter((field) => field.owner !== 'integrator');
    });

    const allFields = computed((): ConfigField[] =>
      (integration.value?.config?.fields || []).filter(
        (field) => field.owner !== 'integrator',
      ));

    // ---- Helpers ----

    // T3 — cancel / back. In modal mode we close the modal instead of navigating.
    const goBack = () => {
      if (props.asModal) {
        emit('close');
        return;
      }
      router.push({ name: 'shop.Integrations' });
    };

    // T3 — successful completion. In modal mode we signal the parent (which
    // reloads its lists) instead of navigating.
    const finishConfig = () => {
      if (props.asModal) {
        emit('done');
        return;
      }
      router.push({ name: 'shop.Integrations' });
    };

    const getLocalizedText = (text: string | Record<string, string> | undefined): string => {
      if (!text) return '';
      if (typeof text === 'string') return text;
      return text[locale.value] || text.fr || text.en || '';
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getFieldLabel = (field: any): string => getLocalizedText(field.label);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getFieldPlaceholder = (field: any): string => getLocalizedText(field.placeholder);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getFieldHelpText = (field: any): string => getLocalizedText(field.helpText);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getFieldOptions = (field: any): any[] => {
      if (!field.options || !Array.isArray(field.options)) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return field.options.map((o: any) => ({ ...o, label: getLocalizedText(o.label) }));
    };

    // ---- Remote data ----

    const loadRemoteData = async (field: ConfigField) => {
      if (!field.remote) return;
      const { resource, label_field, value_field } = field.remote;
      remoteDataLoading[field.key] = true;
      remoteDataError[field.key] = false;
      try {
        const response = await nestCall(
          'get', NestApiType.CLIENT_API,
          `/integrations/${integrationSlug}/remote-data/${resource}`,
          {}, '', undefined, true, {},
        );
        if (response?.statusCode === 200 && Array.isArray(response.data)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          remoteOptions[field.key] = response.data.map((item: any) => ({
            value: String(item[value_field] || ''),
            label: String(item[label_field] || ''),
          }));
        } else {
          remoteDataError[field.key] = true;
        }
      } catch {
        remoteDataError[field.key] = true;
      } finally {
        remoteDataLoading[field.key] = false;
      }
    };

    const loadRemoteFieldsForCurrentStep = () => {
      for (const field of currentFields.value) {
        if (field.remote && !remoteOptions[field.key]) {
          loadRemoteData(field);
        }
      }
    };

    // Watch step changes to load remote data
    watch(() => wizard.currentStepIndex.value, () => {
      loadRemoteFieldsForCurrentStep();
    });

    // ---- Init ----

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const initializeFormData = (configurations: any[] = []): void => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: Record<string, any> = {};
      const configsMap = new Map<string, string>();

      // Collect all fields
      const fields: ConfigField[] = [];
      if (integration.value?.config?.steps) {
        for (const step of integration.value.config.steps) {
          if (step.fields) fields.push(...step.fields);
        }
      } else if (integration.value?.config?.fields) {
        fields.push(...integration.value.config.fields);
      }

      // Defaults
      fields.forEach((field) => {
        if (field.type === 'checkbox') data[field.key] = false;
        else if (field.type === 'number') data[field.key] = field.validation?.min || 0;
        else if (field.type === 'multiselect') data[field.key] = [];
        else data[field.key] = '';
      });

      // Override with existing
      configurations.forEach((config) => {
        const key = config.config_key || config.key;
        const value = config.config_value || config.value;
        if (key) configsMap.set(key, value || '');
        if (key && value !== undefined && value !== null) {
          const field = fields.find((f) => f.key === key);
          if (field) {
            if (field.type === 'checkbox') data[key] = value === 'true' || value === true;
            else if (field.type === 'number') data[key] = typeof value === 'string' ? parseFloat(value) : value;
            else if (field.type === 'multiselect') {
              try { data[key] = typeof value === 'string' ? JSON.parse(value) : value; } catch { data[key] = []; }
            } else data[key] = value;
          } else {
            data[key] = value;
          }
        }
      });

      formData.value = data;
      savedConfigsMap.value = configsMap;
    };

    const loadIntegration = async () => {
      loading.value = true;
      error.value = '';
      try {
        const response = await nestCall(
          'get', NestApiType.CLIENT_API,
          `/integrations/${integrationSlug}/configuration`,
          {}, '', undefined, true, { shop_id: idShop },
        );

        if (response?.statusCode === 200 && response.data) {
          const { integration: integrationData, configurations } = response.data;
          if (integrationData) {
            integration.value = { ...integrationData, config: integrationData.config_schema };

            if (!displayName.value) {
              displayName.value = integrationData.name;
              displayIcon.value = integrationData.icon_url || '';
              displayDescription.value = integrationData.short_description || integrationData.description || '';
            }

            // Init form data first
            initializeFormData(configurations || []);

            // Then set wizard mode
            if (integration.value?.config?.steps && integration.value.config.steps.length > 0) {
              isWizardMode.value = true;
              wizardSteps.value = integration.value.config.steps;
            }

            // Load remote fields for initial step
            loadRemoteFieldsForCurrentStep();
          } else {
            error.value = t('integrations.errors.notFound');
          }
        } else {
          error.value = t('integrations.errors.notFound');
        }
      } catch (err) {
        console.error('Error loading integration:', err);
        error.value = t('integrations.errors.loadConfig');
      } finally {
        loading.value = false;
      }
    };

    // ---- Save ----

    const saveCurrentStepFields = async (): Promise<boolean> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dataToSave: Record<string, any> = {};
      for (const field of currentFields.value) {
        let val = formData.value[field.key];
        if (field.type === 'multiselect' && Array.isArray(val)) val = JSON.stringify(val);
        dataToSave[field.key] = val;
      }

      const response = await nestCall(
        'post', NestApiType.CLIENT_API,
        `/integrations/${integrationSlug}/configuration`,
        {}, '', dataToSave, true, {},
      );

      if (response?.error?.errors) {
        const msgs = applyBackendFieldErrors(response.error.errors);
        await showToastError(msgs);
        return false;
      }
      if (response?.statusCode === 200 || response?.statusCode === 201) {
        // Update local map
        for (const [key, val] of Object.entries(dataToSave)) {
          savedConfigsMap.value = new Map(savedConfigsMap.value).set(key, String(val));
        }
        return true;
      }
      await showToastError(response?.error?.message || t('integrations.errors.configure'));
      return false;
    };

    const testConnectionForStep = async (): Promise<boolean> => {
      isTesting.value = true;
      try {
        const response = await nestCall(
          'post', NestApiType.CLIENT_API,
          `/integrations/${integrationSlug}/test-connection`,
          {}, '', {}, true, {},
        );
        if (response?.statusCode === 200 && response.data?.success) {
          const step = currentWizardStep.value;
          if (step?.on_complete?.success_message) {
            await showToastSuccess(getLocalizedText(step.on_complete.success_message));
          } else {
            await showToastSuccess(t('integrations.config.connectionSuccess'));
          }
          if (step) {
            savedConfigsMap.value = new Map(savedConfigsMap.value).set(`__step:${step.key}:validated`, 'true');
          }
          return true;
        }
        const step = currentWizardStep.value;
        // T4 — never surface the connector's RAW error code (e.g.
        // "connection_failed") to the merchant. Priority: integrator-defined
        // message (manifest `on_complete.error_message`, localized) → generic
        // translated message. The raw code is logged for debugging only.
        if (response?.data?.error) {
          // eslint-disable-next-line no-console
          console.warn(`[integration:${integrationSlug}] test_connection failed:`, response.data.error);
        }
        const errorMsg = (step?.on_complete?.error_message
          ? getLocalizedText(step.on_complete.error_message)
          : '')
          || t('integrations.config.connectionFailed');
        await showToastError(errorMsg);
        return false;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`[integration:${integrationSlug}] test_connection error:`, e);
        const step = currentWizardStep.value;
        const errorMsg = (step?.on_complete?.error_message
          ? getLocalizedText(step.on_complete.error_message)
          : '')
          || t('integrations.config.connectionFailed');
        await showToastError(errorMsg);
        return false;
      } finally {
        isTesting.value = false;
      }
    };

    const validateCurrentFields = (): boolean => {
      // Clear previous errors
      Object.keys(fieldErrors).forEach((key) => {
        delete fieldErrors[key];
      });

      let valid = true;
      currentFields.value.filter((field) => field.required).forEach((field) => {
        const val = formData.value[field.key];
        if (field.type === 'multiselect') {
          if (!Array.isArray(val) || val.length === 0) {
            fieldErrors[field.key] = t('integrations.config.fieldRequired', { field: getFieldLabel(field) });
            valid = false;
          }
        } else if (field.type === 'checkbox') {
          // Checkbox required means it must be checked
          // Usually not the case, skip
        } else if (val === undefined || val === null || val === '') {
          fieldErrors[field.key] = t('integrations.config.fieldRequired', { field: getFieldLabel(field) });
          valid = false;
        }
      });
      return valid;
    };

    const handleSubmit = async () => {
      if (!integration.value) return;

      if (!validateCurrentFields()) return;

      isSaving.value = true;
      try {
        const saved = await saveCurrentStepFields();
        if (!saved) return;

        if (currentWizardStep.value?.on_complete?.action === 'test_connection') {
          const ok = await testConnectionForStep();
          if (!ok) return;
        }

        if (!wizard.isLastStep.value) {
          wizard.next();
        } else {
          await showToastSuccess(t('integrations.success.configured', { name: integration.value.name }));
          setTimeout(() => finishConfig(), 800);
        }
      } catch {
        await showToastError(t('integrations.errors.configure'));
      } finally {
        isSaving.value = false;
      }
    };

    const handleSubmitSimple = async () => {
      if (!integration.value) return;
      isSaving.value = true;
      try {
        // Sérialisation multiselect unifiée avec le mode wizard (G7) : les
        // valeurs multiselect sont sérialisées en JSON avant l'envoi.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dataToSave: Record<string, any> = {};
        for (const field of allFields.value) {
          let val = formData.value[field.key];
          if (field.type === 'multiselect' && Array.isArray(val)) val = JSON.stringify(val);
          dataToSave[field.key] = val;
        }
        const response = await nestCall(
          'post', NestApiType.CLIENT_API,
          `/integrations/${integrationSlug}/configuration`,
          {}, '', dataToSave, true, {},
        );
        if (response?.error?.errors) {
          const msgs = applyBackendFieldErrors(response.error.errors);
          await showToastError(msgs);
        } else if (response?.statusCode === 200 || response?.statusCode === 201) {
          await showToastSuccess(t('integrations.success.configured', { name: integration.value.name }));
          setTimeout(() => finishConfig(), 800);
        } else {
          await showToastError(response?.error?.message || t('integrations.errors.configure'));
        }
      } catch {
        await showToastError(t('integrations.errors.configure'));
      } finally {
        isSaving.value = false;
      }
    };

    onMounted(() => loadIntegration());

    return {
      t,
      loading,
      error,
      integration,
      displayName,
      displayIcon,
      displayDescription,
      formData,
      isSaving,
      isTesting,
      isWizardMode,
      wizardSteps,
      wizard,
      currentWizardStep,
      currentFields,
      allFields,
      hasConfigFields,
      fieldErrors,
      remoteOptions,
      remoteDataLoading,
      remoteDataError,
      getLocalizedText,
      getFieldLabel,
      getFieldPlaceholder,
      getFieldHelpText,
      getFieldOptions,
      loadIntegration,
      loadRemoteData,
      handleSubmit,
      handleSubmitSimple,
      goBack,
    };
  },
});
</script>

<style scoped lang="scss">
.integration-config-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-content {
  padding: 1.5rem;
  flex: 1;
}

/* ============================== */
/* MODAL MODE (T3)                */
/* ============================== */
/* Hosted inside the config modal: the Dialog provides the frame + scroll, so we
   drop the page padding and the card's own border/shadow, and let the wizard
   fill the modal width. */
/* Fixed-chrome modal wizard. The Dialog gives a bounded height; INSIDE it the
   header, the stepper rail and the action footer stay put — ONLY the field
   column scrolls. Everything is left-aligned (the page inherits a centered text
   context inside the Dialog otherwise). */
.is-modal {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  text-align: left;
}
.is-modal .page-content {
  flex: 1;
  min-height: 0;
  padding: 0;
  overflow: hidden;
}

/* Modal top bar (épuré) : logo sobre + marque + sous-titre. Rangée fixe. */
.modal-topbar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1.35rem 1.75rem;
  border-bottom: 1px solid #eceef1;
}
.modal-topbar-logo {
  flex: 0 0 auto;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #e4e7ec;
  color: #9aa1ad;
  font-size: 0.95rem;
  overflow: hidden;
}
.modal-topbar-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 0.4rem;
}
.modal-topbar-heading {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.modal-topbar-brand {
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f1729;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.modal-topbar-sub {
  font-size: 0.76rem;
  color: #9aa1ad;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.modal-topbar-close {
  flex: 0 0 auto;
  margin-left: auto;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: #9aa1ad;
  border-radius: 7px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
}
.modal-topbar-close:hover {
  background: #f4f5f7;
  color: #39415a;
}
.modal-topbar-close:focus-visible {
  outline: 2px solid #94c840;
  outline-offset: 2px;
}

/* The in-card step header is redundant in modal mode (the eyebrow + title live
   in the body head-block now) — hide it. */
.is-modal .wizard-card-header {
  display: none;
}

/* -- Single column: [step tabs] on top, form fills the rest (proposition B) -- */
.is-modal .wizard-layout {
  height: 100%;
  max-width: none;
  margin: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  min-height: 0;
  overflow: hidden;
  flex-direction: column;
}

/* The vertical rail is replaced by the segmented step tabs — hide it in modal. */
.is-modal .wizard-sidebar {
  display: none;
}

/* Refined step indicator (épuré): small numbered dots + thin connectors.
   The dot always shows the step number; state is carried by colour —
   done = accent fill (white number) · active = accent ring (accent number) ·
   upcoming/locked = light + disabled. Every step shows its label. Fixed band
   under the header. */
.wizard-stepper {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: 1.15rem 1.75rem;
  border-bottom: 1px solid #eceef1;
}
.wstep {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}
.wstep-dot {
  flex: 0 0 auto;
  width: 1.55rem;
  height: 1.55rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 600;
  border: 1.5px solid #dcdfe4;
  background: #ffffff;
  color: #9aa1ad;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.wstep-lbl {
  font-size: 0.82rem;
  font-weight: 500;
  color: #8792a2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wstep.is-complete .wstep-lbl {
  color: #39415a;
}
.wstep.is-active .wstep-lbl {
  color: #0f1729;
  font-weight: 600;
}
.wstep.is-complete .wstep-dot {
  border-color: #94c840;
  background: #94c840;
  color: #ffffff;
}
.wstep.is-active .wstep-dot {
  border-color: #94c840;
  background: #ffffff;
  color: #94c840;
}
.wstep.is-locked {
  cursor: not-allowed;
}
.wstep:focus-visible {
  outline: 2px solid #94c840;
  outline-offset: 3px;
  border-radius: 6px;
}
.wstep-bar {
  flex: 1;
  height: 1px;
  min-width: 14px;
  margin: 0 0.7rem;
  background: #e4e7ec;
}
.wstep-bar.done {
  background: #94c840;
}

/* Main column (below the tabs): step header fixed, fields scroll, actions pinned. */
.is-modal .wizard-main {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.is-modal .wizard-card {
  padding: 0;
  overflow: hidden;
}
.is-modal .wizard-card-header {
  flex: 0 0 auto;
  margin: 0;
  padding: 1.5rem 2rem 1.25rem;
  border-bottom: 1px solid #eef1f6;
}
.is-modal .wizard-card-title {
  font-size: 1.25rem;
}
.is-modal .wizard-form {
  overflow: hidden;
  min-height: 0;
}
.is-modal .wizard-fields {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1.75rem 1.75rem 2rem;
  gap: 1.4rem;
}
.is-modal .wizard-locked {
  padding: 2rem;
}
.is-modal .wizard-actions {
  flex: 0 0 auto;
  margin: 0;
  padding: 1.15rem 1.75rem;
  border-top: 1px solid #eceef1;
  background: #ffffff;
}

/* Premium field styling inside the modal (PrimeVue overrides via ::v-deep, so
   inputs match the épuré look: fine border, subtle green focus ring). */
.is-modal :deep(.form-label) {
  font-size: 0.815rem;
  font-weight: 500;
  color: #39415a;
}
.is-modal :deep(.form-hint) {
  color: #9aa1ad;
}
.is-modal :deep(.p-inputtext),
.is-modal :deep(.p-dropdown),
.is-modal :deep(.p-multiselect),
.is-modal :deep(.p-password-input) {
  border-radius: 8px;
  border-color: #dcdfe4;
  font-size: 0.9rem;
  color: #0f1729;
}
.is-modal :deep(.p-inputtext),
.is-modal :deep(.p-password-input) {
  padding: 0.6rem 0.85rem;
}
.is-modal :deep(.p-inputtext:enabled:focus),
.is-modal :deep(.p-password-input:enabled:focus),
.is-modal :deep(.p-dropdown:not(.p-disabled).p-focus),
.is-modal :deep(.p-multiselect:not(.p-disabled).p-focus) {
  border-color: #94c840;
  box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.15);
}

/* Simple mode (flat fields, no steps): fields scroll, actions handled inline. */
.is-modal .simple-layout {
  height: 100%;
  max-width: none;
  margin: 0;
  padding: 1.5rem 2rem;
  overflow-y: auto;
  gap: 1.25rem;
}

/* ============================== */
/* States                         */
/* ============================== */
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 2rem;
  gap: 1rem;
  color: #9ca3af;

  i { font-size: 2.5rem; opacity: 0.4; }
  p { margin: 0; font-size: 0.9375rem; }
}

.error-state i { color: #dc2626; }

/* ============================== */
/* WIZARD LAYOUT                  */
/* ============================== */
.wizard-layout {
  display: flex;
  max-width: 1100px;
  margin: 0 auto;
  gap: 0;
  min-height: 500px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

/* ---- Sidebar ---- */
.wizard-sidebar {
  width: 280px;
  flex-shrink: 0;
  background: #f8fafc;
  border-right: 1px solid #e5e7eb;
  padding: 1.75rem 1.5rem;
  display: flex;
  flex-direction: column;
}

.wizard-sidebar-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.wizard-logo {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 8px;
  background: linear-gradient(135deg, #94c840 0%, #7fb32e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.125rem;
  overflow: hidden;
  flex-shrink: 0;

  &.has-image {
    background: #fff;
    border: 1px solid #e5e7eb;
    padding: 0.125rem;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 6px;
  }
}

.wizard-sidebar-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
}

/* ---- Stepper ---- */
.stepper {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.stepper-item {
  display: flex;
  gap: 0.875rem;
  cursor: pointer;
  padding: 0.25rem 0;
  transition: opacity 0.15s;

  &.is-locked {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:not(.is-locked):hover .stepper-label {
    color: #1f2937;
  }
}

.stepper-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.stepper-circle {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  border: 2px solid #d1d5db;
  background: #ffffff;
  color: #9ca3af;
  transition: all 0.2s;

  .is-active & {
    border-color: #94c840;
    background: #94c840;
    color: #ffffff;
    box-shadow: 0 0 0 4px rgba(148, 200, 64, 0.15);
  }

  .is-complete & {
    border-color: #94c840;
    background: #94c840;
    color: #ffffff;
  }

  .is-locked & {
    border-color: #e5e7eb;
    background: #f3f4f6;
    color: #d1d5db;

    i { font-size: 0.625rem; }
  }
}

.stepper-line {
  width: 2px;
  flex: 1;
  min-height: 1.5rem;
  background: #e5e7eb;
  margin: 0.25rem 0;

  .is-complete & {
    background: #94c840;
  }
}

.stepper-content {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0 1.25rem;
  min-width: 0;
}

.stepper-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6b7280;
  transition: color 0.15s;

  .is-active & {
    color: #1f2937;
    font-weight: 600;
  }

  .is-complete & {
    color: #94c840;
  }
}

.stepper-desc {
  font-size: 0.6875rem;
  color: #9ca3af;
  margin-top: 0.125rem;
  line-height: 1.4;

  .is-active & {
    color: #6b7280;
  }
}

/* ---- Main area ---- */
.wizard-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.wizard-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 2rem 2.5rem;
}

.wizard-card-header {
  margin-bottom: 2rem;
}

.wizard-step-badge {
  display: inline-block;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94c840;
  background: rgba(148, 200, 64, 0.1);
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  margin-bottom: 0.75rem;
}

.wizard-card-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.375rem;
}

.wizard-card-desc {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

/* Locked state */
.wizard-locked {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #9ca3af;

  p { margin: 0; font-size: 0.875rem; }
}

.wizard-locked-icon {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: #d1d5db;
}

/* ---- Form ---- */
.wizard-form {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.wizard-fields {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  flex: 1;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
}

.form-required {
  color: #dc2626;
  margin-left: 0.125rem;
}

.form-control {
  width: 100%;
}

:deep(.p-password) {
  width: 100%;
  .p-password-input, input { width: 100%; }
}

.form-switch {
  padding: 0.375rem 0;
}

.form-hint {
  font-size: 0.75rem;
  color: #9ca3af;
  line-height: 1.4;
}

.form-error {
  font-size: 0.75rem;
  color: #dc2626;
  line-height: 1.4;
}

:deep(.p-invalid) {
  border-color: #dc2626 !important;
}

.remote-placeholder {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #9ca3af;
}

.remote-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #dc2626;
}

/* ---- Actions ---- */
.wizard-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.5rem;
  margin-top: 2rem;
  border-top: 1px solid #f3f4f6;
}

.wizard-actions-spacer {
  flex: 1;
}

/* ============================== */
/* SIMPLE LAYOUT                  */
/* ============================== */
.simple-layout {
  max-width: 700px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.simple-header-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.25rem;
  }

  p {
    font-size: 0.8125rem;
    color: #6b7280;
    margin: 0;
  }
}

.simple-logo {
  width: 3rem;
  height: 3rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #94c840 0%, #7fb32e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.5rem;
  overflow: hidden;
  flex-shrink: 0;

  &.has-image {
    background: #fff;
    border: 1px solid #e5e7eb;
    padding: 0.25rem;
  }

  img { width: 100%; height: 100%; object-fit: contain; }
}

.simple-form-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  h2 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.375rem;
  }
}

.simple-form-desc {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0 0 1.75rem;
}

/* ============================== */
/* Responsive                     */
/* ============================== */
@media (max-width: 768px) {
  .wizard-layout {
    flex-direction: column;
  }

  .wizard-sidebar {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    padding: 1.25rem;
  }

  .stepper {
    flex-direction: row;
    gap: 0.5rem;
    overflow-x: auto;
  }

  .stepper-item {
    flex-direction: column;
    align-items: center;
    min-width: 80px;
  }

  .stepper-indicator {
    flex-direction: row;
  }

  .stepper-line {
    width: auto;
    height: 2px;
    min-height: 0;
    min-width: 1.5rem;
    margin: 0 0.25rem;
  }

  .stepper-content {
    padding: 0;
    align-items: center;
  }

  .stepper-desc {
    display: none;
  }

  .wizard-card {
    padding: 1.5rem;
  }

  .wizard-actions {
    flex-direction: column-reverse;
    gap: 0.5rem;

    :deep(button) {
      width: 100%;
    }
  }
}
</style>
